import {useCallback, useEffect, useState} from 'react';
import {useParams} from 'react-router-dom';
import {Box, Card, Grid, Stack, Typography} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {ActivitiesSectorSettings} from '../../../../../../backend/interfaces/activities-sector-settings';
import GenericHeader from '../../../../../../components/generic-header/generic-header';
import {ScreenPaths} from '../../../../../../enums';
import {translations} from '../../../../../../i18n/translations';
import {EntityActivityTypesSettingsRepository} from '../../../../../../repositories/entity-activity-types-settings';
import {CardCollapse} from '../../../../../../components/shared/card-collapse';
import {ReactSVG} from 'react-svg';
import Switch from '../../../../../../components/switch/switch';
import {FlatStartsAtTime} from '../../../../../../components/flat-starts-at-time/flat-starts-at-time';

interface EntityActivitySectorSettingsParams {
  entityId: string;
  activitiesSectorId: string;
}

export default function EntityActivitySectorSettings() {
  // Styles
  const subtitleSx = {
    fontSize: '18px',
    fontWeight: '400',
    color: 'var(--dcm-white-base)',
  };

  const groupCardSx = {
    display: 'flex',
    flexDirection: 'column',
    padding: '24px',
    gap: '20px',
  };

  const groupCardTitleSx = {
    color: 'var(--dcm-gray)',
    fontSize: '22px',
    fontWeight: '700',
    lineHeight: '18px',
  };

  const groupTagCardSx = {
    boxShadow: 'none',
    '& .MuiCardActions-root': {
      padding: '8px 12px',
      backgroundColor: 'var(--dcm-bgseniormenu)',
    },
    '& .MuiCardContent-root': {
      marginTop: '12px',
      padding: '0 !important',
    },
  };

  const groupTagCardIconSx = {
    width: '42px',
    height: '42px',
    color: 'var(--dcm-gray)',
    svg: {
      width: '100%',
      height: '100%',
      path: {
        fill: 'currentColor',
      },
    },
  };

  const groupTagCardTitleSx = {
    color: 'var(--dcm-gray)',
    fontSize: '20px',
    fontWeight: '500',
    lineHeight: '18px',
  };

  const gridRowSx = {
    padding: '0 12px',
    height: '46px',
    alignItems: 'center',
  };

  const gridHeaderSx = {
    borderBottom: '1px solid rgba(228, 227, 227, 0.50)',
    ...gridRowSx,
  };

  const gridHeaderTitleSx = {
    color: 'var(--dcm-gray)',
    fontSize: '18px',
    fontWeight: '500',
    lineHeight: '18px',
  };

  const gridRowTitleSx = {
    color: 'var(--dcm-gray)',
    fontSize: '16px',
    fontWeight: '500',
    lineHeight: '18px',
  };

  // Translations
  const {t} = useTranslation();

  // State
  const [activitiesSectorSettings, setActivitiesSectorSettings] =
    useState<ActivitiesSectorSettings>();

  // Get params
  const {entityId, activitiesSectorId}: EntityActivitySectorSettingsParams = useParams();

  const loadActivitySectorSettings = useCallback(() => {
    EntityActivityTypesSettingsRepository.instance
      .getEntityActivitiesSectorSettings(entityId, activitiesSectorId)
      .then((settings) => {
        setActivitiesSectorSettings(settings);
      })
      .catch((err) => {
        console.error(`Error: ${err}`);
      });
  }, [activitiesSectorId, entityId]);

  const titleCrumbs = [
    {
      label: t(
        translations.office.professional_caregiver.settings.generic_header.breadcrumbs.settings
      ),
      link: `${ScreenPaths.Office}${ScreenPaths.Office_ProfessionalCaregiver}/${entityId}${ScreenPaths.ProfessionalCaregiver_Settings}`,
    },
    {
      label: activitiesSectorSettings?.name || '',
      link: `${ScreenPaths.Office}${ScreenPaths.Office_ProfessionalCaregiver}/${entityId}${
        ScreenPaths.ProfessionalCaregiver_Settings
      }${ScreenPaths.ProfessionalCaregiver_ActivitySectorSettings.replace(
        ':activitiesSectorId',
        activitiesSectorId
      )}`,
    },
  ];

  useEffect(() => {
    loadActivitySectorSettings();
  }, [loadActivitySectorSettings]);

  return (
    <Stack gap={2}>
      <GenericHeader
        titleCrumbs={titleCrumbs}
        isLoading={!activitiesSectorSettings}
        leftColumn={
          <Typography sx={subtitleSx} noWrap>
            {t(
              translations.office.professional_caregiver.settings.generic_header.subtitle
                .activities_sector
            )}
          </Typography>
        }
      />
      {activitiesSectorSettings?.activitiesGroups?.map((activitiesGroup) => (
        <Card key={activitiesGroup.id} sx={groupCardSx}>
          <Stack direction="row" alignItems="center" gap={1}>
            <Box sx={groupTagCardIconSx}>
              <ReactSVG src={activitiesGroup.iconUrl} />
            </Box>
            <Typography variant="h5" sx={groupCardTitleSx}>
              {activitiesGroup.name}
            </Typography>
          </Stack>
          {activitiesGroup.tags.map((activitiesGroupTag) => {
            const hasStartAtTime = activitiesGroupTag.items?.some((item) => item.startsAtTime);
            const hasIsActive = activitiesGroupTag.items?.some((item) => item.isActive);
            console.log(activitiesGroupTag.name, hasStartAtTime);
            return (
              <CardCollapse
                key={activitiesGroupTag.id}
                collapsableHeader={
                  <Typography variant="h6" sx={groupTagCardTitleSx}>
                    {activitiesGroupTag.name}
                  </Typography>
                }
                cardProps={{sx: {...groupTagCardSx}}}
              >
                <Grid container gap={1}>
                  <Grid container sx={gridHeaderSx}>
                    <Grid item xs={3} />
                    {hasIsActive && (
                      <Grid item xs={1}>
                        <Typography sx={gridHeaderTitleSx}>
                          {t(translations.institution_settings_screen.columns.active)}
                        </Typography>
                      </Grid>
                    )}
                    {hasStartAtTime && (
                      <Grid item xs={8}>
                        <Typography sx={gridHeaderTitleSx}>
                          {t(translations.care_services.form.start_time_label)}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                  {activitiesGroupTag.items?.map((activitiesGroupTagItem) => (
                    <Grid container sx={gridRowSx}>
                      <Grid item xs={3}>
                        <Typography sx={gridRowTitleSx}>{activitiesGroupTagItem.name}</Typography>
                      </Grid>
                      {activitiesGroupTagItem.isActive !== undefined && (
                        <Grid item xs={1}>
                          <Switch value={activitiesGroupTagItem.isActive} />
                        </Grid>
                      )}
                      {activitiesGroupTagItem.startsAtTime && (
                        <Grid item xs={8}>
                          <FlatStartsAtTime
                            value={activitiesGroupTagItem.startsAtTime}
                            errors={''}
                            sx={{width: '220px'}}
                          />
                        </Grid>
                      )}
                    </Grid>
                  ))}
                </Grid>
              </CardCollapse>
            );
          })}
        </Card>
      ))}
    </Stack>
  );
}
